import type { Location } from 'history';

enum URL_OPERATIONS {
  Append = 'append',
  Get = 'get',
  Set = 'set',
  FilteredAppend = 'filteredAppend',
}

type ParamsObj = Record<string, string>;

/**
 * Given a URL
 */
export function getParam(url: string, param: string): string | null {
  const u = new URL(url, window.location.origin);
  return u.searchParams.get(param);
}

/**
 * Create a higher-order function that can create param utilities based on operation type
 * Note: the origin param is defaulted for testing
 */
export function createParamUtil(type: string, origin: string = window.location.origin) {
  /**
   * Given a hash of param name/value, build them onto a url
   */
  return function addParamsToUrl(url: string, params: ParamsObj): string {
    const u = new URL(url, origin);
    Object.entries(params).forEach(([name, value]) => {
      const shouldFilter = type === URL_OPERATIONS.FilteredAppend;

      if (shouldFilter && (value === undefined || value === null || value === '')) {
        return;
      }

      if (type === URL_OPERATIONS.Append || type === URL_OPERATIONS.FilteredAppend) {
        u.searchParams.append(name, value);
      }

      if (type === URL_OPERATIONS.Set) {
        u.searchParams.set(name, value);
      }
    });

    return `${u.pathname}?${u.searchParams.toString()}`;
  };
}

/**
 * **getUrlParams**_(url)_<br>
 * Get parameters as an object from a url's query params
 * @param { string } search: A url's search section like ?userId=bruce@batman.com
 * @returns { Object } An object with query params as keys and values
 */
export const getUrlParams = (search: string): Record<string, string> => {
  const hashes = search.slice(search.indexOf('?') + 1).split('&');
  const params: Record<string, string> = {};

  hashes.forEach((hash) => {
    const [key, val] = hash.split('=');
    params[key] = decodeURIComponent(val);
  });

  return params;
};

export const addParams = createParamUtil(URL_OPERATIONS.Append);
export const setParams = createParamUtil(URL_OPERATIONS.Set);
export const addFilteredParams = createParamUtil(URL_OPERATIONS.FilteredAppend);

/**
 * Returns a filtered searchString containing only the allowedKeys
 * @param {string} searchString - generally location.search from react-router-dom (with or without leading '?')
 * @param {string[]} allowedKeys - allowed parameter keys
 * @returns {string} a filtered searchString (with a leading '?' if the searchString has any remaining keys)
 */
export const filterUrlParams = (searchString: string, allowedKeys: string[] = []) => {
  const searchParams = new URLSearchParams(searchString);
  const keysToDelete: string[] = [];
  searchParams.forEach((_value, key) => {
    if (!allowedKeys.includes(key)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((value) => {
    searchParams.delete(value);
  });
  const newSearchString = searchParams.toString();
  return newSearchString ? `?${newSearchString}` : '';
};

export const URL_LIMITS = {
  DEV: 4096,
  PROD: 8192,
};

/**
 * Allow navigation if only search params or hash changed (ignoring ignoredParams)
 * @param {Location} currentLoc - The current location
 * @param {Location} nextLoc - The next location
 * @param {string[]} ignoredParams - The parameters to ignore in the comparison
 * @returns {boolean} - True if the navigation is a parameter-only change, false otherwise
 */
export const isParameterOnlyChange = (
  currentLoc: Location,
  nextLoc: Location,
  ignoredParams: string[],
): boolean => {
  // Must be same pathname and hash
  if (currentLoc.pathname !== nextLoc.pathname || currentLoc.hash !== nextLoc.hash) {
    return false;
  }

  const currentParams = new URLSearchParams(currentLoc.search);
  const nextParams = new URLSearchParams(nextLoc.search);

  // Remove ignoredParams from comparison
  ignoredParams.forEach((param) => {
    currentParams.delete(param);
    nextParams.delete(param);
  });

  // All other parameters must be identical
  return currentParams.toString() === nextParams.toString();
};
